import { Tag as TagType } from '@payaca/types/tagTypes';
import { FC, useState, type JSX } from 'react';

import { Button } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import MiniLoader from '../miniLoader/MiniLoader';
import Tag from '../tag/Tag';
import AddTagControl from './AddTagControl';
import './TagControl.sass';

export enum TagControlAlignment {
  LEFT = 'left',
  RIGHT = 'right',
}

type Props = {
  label?: string;
  availableTags?: TagType[];
  tags: TagType[];
  isReadOnly?: boolean;
  isAddingTag?: boolean;
  isRemovingTag?: boolean;
  onRemoveTag?: (tagId: number) => void;
  onAddAvailableTag?: (tagId: number) => void;
  alignment?: TagControlAlignment;
};
const TagControl: FC<Props> = ({
  label,
  availableTags = [],
  tags,
  isAddingTag = false,
  isReadOnly = true,
  isRemovingTag = false,
  onRemoveTag,
  onAddAvailableTag,
  alignment = TagControlAlignment.RIGHT,
}: Props): JSX.Element => {
  const [showAddTagControl, setShowAddTagControl] = useState(false);
  const [removingTagId, setRemovingTagId] = useState<number>();
  const [hasMadeAddTagRequest, setHasMadeAddTagRequest] = useState(false);

  return (
    <div className={`tag-control ${alignment}-alignment`}>
      <div className="tag-control-inner">
        {tags.map((tag, i) => {
          return (
            <Tag
              colour={tag.tagColour}
              key={`tag-${i}`}
              isProcessing={removingTagId === tag.id && isRemovingTag}
              isRemovable={!isReadOnly}
              onRemoveTag={
                !isReadOnly && onRemoveTag
                  ? () => {
                      setRemovingTagId(tag.id);
                      onRemoveTag(tag.id);
                    }
                  : undefined
              }
            >
              {tag.tagText}
            </Tag>
          );
        })}
        {!isReadOnly && !!onAddAvailableTag && (
          <>
            {isAddingTag && hasMadeAddTagRequest && <MiniLoader />}
            {!(isAddingTag && hasMadeAddTagRequest) && (
              <>
                {showAddTagControl && (
                  <AddTagControl
                    availableTags={availableTags}
                    onAddAvailableTag={(tagId) => {
                      setHasMadeAddTagRequest(true);
                      onAddAvailableTag(tagId);
                      setShowAddTagControl(false);
                    }}
                    onBlur={() => setShowAddTagControl(false)}
                    userCanCreateNewTag={false}
                  />
                )}
                {!showAddTagControl && (
                  <>
                    {!tags.length && (
                      <small className="add-tags-prompt">
                        {label || 'Add tags'}
                      </small>
                    )}
                    <Button
                      aria-label="Add tag"
                      variant="secondary"
                      size="xs"
                      onClick={() => setShowAddTagControl(true)}
                    >
                      <UntitledIcon name="plus.3" className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TagControl;
